import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ActionBar from '../../../../components/ui/ActionBar';
import { AG_RECRUITMENT_MEMBER_STATUS, ALERT_TYPE, ONBOARDING_STATUS } from '../../../../constants';
import { getAdvisoryGroupSelectedMembers, getMemberComments, resetPanelSelection, getMemberRetirementReasons } from '../../../../ducks/advisoryGroup';
import { Button } from 'react-bootstrap';
import Icon from '../../../../components/ui/Icon';
import DataGrid from '../../../../components/DataGrid';
import {
    formatPhoneNumber,
    pickProps,
    sortingFnDateOnly,
    toDate,
} from '../../../../utils/utils';
import GridButton from '../../../../components/ui/GridButton';
import MemberDetailsModal from '../../operations/operation-details/MemberDetailsModal';
import CommentModal from '../../operations/operation-details/CommentModal';
import Loader from '../../../../components/ui/Loader';
import MemberRetireModal from './MemberRetireModal';
import { AlertContext } from '../../../../context/AlertContext';
import messages from '../../../../utils/helper/messages';
import api from '../../../../services/api/advisoryGroupService';

const SelectedPanel = ({ selectedAgId }) => {
    const dispatch = useDispatch();
    const { showAlert } = useContext(AlertContext);

    const currentUser = useSelector((state) => state.user.currentUser);
    const selectedMemberData = useSelector((state) => state.advisoryGroup?.panelSelectedMembers);

    const [masterChecked, setMasterChecked] = useState(false);
    const [checkedIds, setCheckedIds] = useState([]);
    const [showEditModal, setShowEditModal] = useState(false);
    const [memberData, setMemberData] = useState(null);
    const [showComment, setShowComment] = useState(null);
    const [currentRowData, setCurrentRowData] = useState(null);
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [openRetireModal, setOpenRetireModal] = useState(false);
    const [retireMemberData, setRetireMemberData] = useState(null);

    useEffect(() => {
        if (selectedAgId && selectedAgId !== 0) {
            dispatch(resetPanelSelection());
            dispatch(getAdvisoryGroupSelectedMembers(selectedAgId));
        }
    }, [selectedAgId]);

    useEffect(() => {
        if (selectedMemberData?.length > 0) {
            const enrolledSelectedMembers = selectedMemberData.filter(mem => mem.memberStatus === AG_RECRUITMENT_MEMBER_STATUS.Enrolled);
            setSelectedMembers(enrolledSelectedMembers);
        } else {
            setSelectedMembers([]);
        }

    }, [selectedMemberData]);

    const rowAction = ({ row }) => {
        return (
            <div className="d-flex">
                <GridButton
                    variant="light"
                    size="sm"
                    className="me-1 my-1"
                    title="Edit"
                    onClick={() => handleEdit(row.original)}>
                    <Icon
                        icon="edit"
                        size={14}
                    />
                </GridButton>
                <GridButton
                    variant="light"
                    size="sm"
                    className="me-1 my-1"
                    title="Retire"
                    onClick={() => handleRetireMemberModal(row.original)}>
                    <Icon
                        icon="retire"
                        size={14}
                    />
                </GridButton>
                <GridButton
                    variant="light"
                    size="sm"
                    className="me-1 my-1"
                    title="Comments"
                    onClick={() => handleCommentModalOpen(row.original)}>
                    <Icon
                        icon="comments"
                        size={14}
                    />
                </GridButton>
            </div>
        );
    };

    const handleRetireMemberModal = (data) => {
        dispatch(getMemberRetirementReasons());
        const retireMemberData = pickProps(data, ['id']);
        setRetireMemberData({ ...retireMemberData });
        setOpenRetireModal(true);
    };

    const handleRetireModalClose = () => {
        setOpenRetireModal(false);
        setRetireMemberData(null);
        dispatch(getAdvisoryGroupSelectedMembers(selectedAgId));
    };

    const handleEdit = (data) => {
        const rowData = { ...data, aGId: data?.agId };
        setMemberData(rowData);
        setShowEditModal(true);
        dispatch(getMemberComments(data?.id));
    };

    const handleCommentModalOpen = (data) => {
        const rowData = { ...data, aGId: data?.agId };
        setShowComment(true);
        setCurrentRowData(rowData);
        dispatch(getMemberComments(data?.id));
    };

    const handleCommentModalClose = () => {
        setShowComment(false);
        setCurrentRowData(null);
    };

    const handleCancel = () => {
        setShowEditModal(false);
    };

    const handleCheckboxChange = (staffId, event) => {
        if (event.target.checked) {
            if (!checkedIds.includes(staffId)) {
                setCheckedIds((prevIds) => [...prevIds, staffId]);
            }
        } else if (checkedIds.includes(staffId)) {
            setCheckedIds((prevIds) =>
                prevIds.filter((prevId) => prevId !== staffId)
            );
        }
    };

    const handleMasterCheckboxChange = (event) => {
        if (event.target.checked) {
            const pendingIds = selectedMembers
                .filter(
                    (staff) => staff.onboarding?.trim() === ONBOARDING_STATUS.Pending
                )
                .map((staff) => staff.id);
            setCheckedIds(pendingIds);
            setMasterChecked(true);
        } else {
            setCheckedIds([]);
            setMasterChecked(false);
        }
    };

    const rowActions = ({ row }) => {
        const { onboarding, id } = row.original;

        if (onboarding?.trim() !== ONBOARDING_STATUS.Pending) return null;

        return (
            <label
                key={id}
                htmlFor={`input-${id}`}>
                <input
                    id={`input-${id}`}
                    type="checkbox"
                    checked={checkedIds.includes(id)}
                    onChange={(event) => handleCheckboxChange(id, event)}
                />
            </label>
        );
    };

    const rowActionHeader = () => {
        const hasPending = selectedMembers?.some(
            (mem) => mem.onboarding?.trim() === ONBOARDING_STATUS.Pending
        );

        return (
            <label
                key={0}
                htmlFor="masterCheck">
                <input
                    id="masterCheck"
                    type="checkbox"
                    checked={masterChecked}
                    disabled={!hasPending}
                    onChange={handleMasterCheckboxChange}
                />
            </label>
        );
    };

    const selectionMemberColumns = [
        {
            id: 'select',
            header: rowActionHeader,
            cell: rowActions,
            size: 32,
            minSize: 32,
            enableSorting: false,
        },
        {
            id: 'firstName',
            header: 'Name',
            accessorKey: 'firstName',
            accessorFn: (row) => row.firstName ? `${row.firstName} ${row.lastName}` : null,
            size: 200,
        },
        {
            id: 'birthday',
            header: 'DOB',
            accessorFn: (row) => toDate(row.birthday),
            sortingFn: sortingFnDateOnly,
            sortType: 'date',
            size: 200,
        },
        {
            id: 'city',
            header: 'City',
            accessorKey: 'city',
            sortingFn: 'text',
            size: 200,
        },
        {
            id: 'phone',
            header: 'Phone',
            accessorKey: 'phone',
            cell: ({ row }) => (
                <div className="text-nowrap">
                    {formatPhoneNumber(row.original.phone)}
                </div>
            ),
            size: 200,
        },
        {
            id: 'onboarding',
            header: 'Onboarding Status',
            accessorKey: 'onboarding',
            sortType: 'text',
            size: 200,
        },
        {
            name: 'actions',
            header: 'Actions',
            cell: rowAction,
            size: 90,
        },
    ];

    const handleSuccess = () => {
        dispatch(getAdvisoryGroupSelectedMembers(selectedAgId));
        showAlert(messages.success.onboardingStatusMessage, ALERT_TYPE.SUCCESS);
        setMasterChecked(false);
        setCheckedIds([]);
    };

    const handleError = (error) => showAlert(`${messages.error.prefix} ${error}.`, ALERT_TYPE.ERROR);

    const handleOnboardingStatus = () => {
        const agMemberData = checkedIds.map((id) => ({
            id,
            onboarding: ONBOARDING_STATUS.Complete,
            lastUserToUpdateRecord: currentUser?.id,
        }));
        api.UpdateOnboardingStatuses(selectedAgId, agMemberData)
            .then(handleSuccess)
            .catch(handleError);
    };

    return (
        <div className="px-3">
            <ActionBar>
                <Button
                    type="button"
                    className="me-2"
                    variant="primary"
                    disabled={checkedIds?.length === 0}
                    onClick={handleOnboardingStatus}>
                    Complete Onboarding
                </Button>
            </ActionBar>
            {selectedAgId !== 0 && selectedMemberData === null ? (
                <Loader />
            ) : (
                <DataGrid
                    columns={selectionMemberColumns}
                    data={selectedMembers || []}
                    pagination={false}
                    gridContainerClass="grid-with-scroll overflow-auto"
                    enableFilters={false}
                    stickyHeader
                />
            )}

            <MemberDetailsModal
                onCancel={handleCancel}
                data={memberData}
                show={showEditModal}
            />

            {showComment && (
                <CommentModal
                    show={showComment}
                    onCancel={handleCommentModalClose}
                    data={currentRowData}
                />
            )}

            {openRetireModal && (
                <MemberRetireModal
                    show={openRetireModal}
                    data={retireMemberData}
                    onCancel={handleRetireModalClose}
                />
            )}
        </div>
    );
};

export default SelectedPanel;
